import type { AxiosInstance } from 'axios'
import type { TrainingDiaryType, TrainingDiaryBody } from 'src/types/diary'
import { castFoodConsumption, type FoodConsumptionBody, type FoodConsumptionType } from 'src/types/foodConsumption'
import { castTrainingParams, type TrainingDiariesParams } from 'src/types/api'
import { parseDate } from 'src/utils/datetime'


export class DiaryApi {
  api: AxiosInstance

  urls = {
    foodConsumptions: () => 'food-consumption/' as const,
    foodConsumption: (id: FoodConsumptionType['id']) => `food-consumption/${id}/`,
    trainingDiaries: () => 'training-diaries/' as const,
    trainingDiaryItem: (id: TrainingDiaryType['id']) => `training-diaries/${id}/`,
  }

  constructor (api: AxiosInstance) {
    this.api = api
  }

  getFoodConsumption = async (params: TrainingDiariesParams): Promise<FoodConsumptionType[]> => {
    const response = await this.api.get<FoodConsumptionType[]>(
      this.urls.foodConsumptions(),
      { params: castTrainingParams(params) },
    )
    return response.data.map(castFoodConsumption)
  }

  postFoodConsumption = async (body: FoodConsumptionBody): Promise<FoodConsumptionType> => {
    const response = await this.api.post<FoodConsumptionType>(this.urls.foodConsumptions(), body)
    return castFoodConsumption(response.data)
  }

  getTrainingDiaries = async (params: TrainingDiariesParams): Promise<TrainingDiaryType[]> => {
    const response = await this.api.get<TrainingDiaryType[]>(
      this.urls.trainingDiaries(),
      { params: castTrainingParams(params) },
    )
    return response.data.map(row => ({...row, datetime: parseDate(row.datetime)}))
  }

  postTrainingDiary = async (body: TrainingDiaryBody): Promise<TrainingDiaryType> => {
    const response = await this.api.post<TrainingDiaryType>(this.urls.trainingDiaries(), body)
    const result = {...response.data}
    result.datetime = parseDate(response.data.datetime)
    return result
  }

  deleteFoodConsumption = async (id: FoodConsumptionType['id']) => {
    return await this.api.delete(this.urls.foodConsumption(id))
  }

  deleteTrainingDiaryItem = async (id: TrainingDiaryType['id']) => {
    return await this.api.delete(this.urls.trainingDiaryItem(id))
  }
}
